/**
 * DeliveryRequest
 * @author ftgibran
 */
import {
  $,
  ID,
  IResource,
  Model,
  ResponseFill,
  HttpIgnore,
  ValidationCNPJ,
  ValidationRequired, ObjectCollection, Collection,
} from 'simpli-web-sdk'
import moment from 'moment'
import {DeliveryStatus} from '@/enums/DeliveryStatus'
import {DeliveryEvent} from '@/enums/DeliveryEvent'
import {DeliveryAudit} from '@/enums/DeliveryAudit'
import {DeliverySlaStatus} from '@/enums/DeliverySlaStatus'
import {DeliveryDriverStatus} from '@/enums/DeliveryDriverStatus'
import Motorista from '@/model/resource/Motorista'
import {ItrackCollection} from '@/helpers'
import * as Helper from '@/helpers'
import {UF} from '@/enums/UF'
import {Region} from '@/enums/Region'
import TipoOcorrencia from '@/model/resource/TipoOcorrencia'
import {TipoSistema} from '@/enums/TipoSistema'
import {DeliveryAuditGeo} from '@/enums/DeliveryAuditGeo'
import {cloneDeep} from 'lodash'

@HttpIgnore()
export default class DeliveryRequest extends Model {
  readonly $name: string = 'DeliveryRequest'

  idFilial = Helper.getIdFilial()

  @ResponseFill('start')
  startDate: string | null = null

  endDate: string | null = null

  @ResponseFill()
  get end() {
    // we should send the next day to the server, the prop endDate is used only to control what the user input
    return !this.endDate ? this.endDate : moment(this.endDate).add(1, 'day').format()
  }

  set end(val) {
    if (val) {
      // in the way back we need to subtract, so the params in the route dont mess up
      this.endDate = moment(val).subtract(1, 'day').format()
    }
  }

  @ResponseFill('dateBy')
  dateBy: string | null = null

  @ResponseFill('idMotoristaPk')
  idMotorista: ID | null = null

  @ResponseFill('status')
  status: string | null = null

  @ResponseFill('ocorrencia')
  event: DeliveryEvent | null = null

  @ResponseFill('auditoria')
  audit: DeliveryAudit | null = null

  @ResponseFill('auditoriaGeo')
  auditGeo: DeliveryAuditGeo | null = null

  @ResponseFill('slaPendente')
  slaStatus: DeliverySlaStatus | null = null

  @ResponseFill('comMotorista')
  driverStatus: DeliveryDriverStatus | null = null

  @ResponseFill('cep')
  cep: string | null = null

  @ResponseFill('cepInicial')
  cepInicial: string | null = null

  @ResponseFill('cepFinal')
  cepFinal: string | null = null

  cte: string | null = null

  @ResponseFill()
  cidade: string | null = null

  @ResponseFill('cnpjEmbarcadorTransportador')
  @ValidationCNPJ()
  documentResponsavel: string | null = null

  @ResponseFill()
  @ValidationCNPJ()
  documentResponsavel2: string | null = null

  @ResponseFill('cnpj')
  @ValidationCNPJ()
  documentDestinatario: string | null = null

  @ResponseFill('nroFiscal')
  docs: string | null = null

  @ResponseFill()
  @ValidationRequired()
  uf: UF | null = null

  @ResponseFill()
  @ValidationRequired()
  region: Region | null = null

  @ResponseFill('idTipoOcorrencia')
  @ValidationRequired()
  idTipoOcorrencia: number | null = null

  @ResponseFill()
  sigla: string | null = null

  @ResponseFill()
  placaVeiculo: string | null = null

  @ResponseFill('idCentroDeCusto')
  @ValidationRequired()
  idCentroDeCusto: number | null = null

  @ResponseFill()
  idInsumo: number | null = null

  @ResponseFill('idMotivoAuditoria')
  @ValidationRequired()
  idMotivoAuditoria: number | null = null

  @ResponseFill('idTipoCriacao')
  @ValidationRequired()
  idTipoCriacao: number | null = null

  @ResponseFill('idTipoFinalizacao')
  @ValidationRequired()
  idTipoFinalizacao: number | null = null

  @ResponseFill()
  outroResponsavel: ID | null = null

  @ResponseFill('cte')
  searchByCTE: string | null = null

  @ResponseFill('dataEntregaInicio')
  dataEntregaInicio: string | null = null

  dataEntregaFim: string | null = null

  @ResponseFill()
  manifesto: string | null = null

  @ResponseFill()
  nomeDestinatario: string | null = null

  @ResponseFill('dataEntregaFim')
  get endEntrega() {
    return this.dataEntregaFim ? moment(this.dataEntregaFim).add(1, 'day').format() : this.dataEntregaFim
  }
  set endEntrega(val) {
    if (val) {
      this.dataEntregaFim = moment(val).subtract(1, 'day').format()
    }
  }

  @ResponseFill('dataPrevisaoInicio')
  dataPrevisaoInicio: string | null = null

  dataPrevisaoFim: string | null = null

  @ResponseFill('dataPrevisaoFim')
  get endPrevisao() {
    return this.dataPrevisaoFim ? moment(this.dataPrevisaoFim).add(1, 'day').format() : this.dataPrevisaoFim
  }
  set endPrevisao(val) {
    if (val) {
      this.dataPrevisaoFim = moment(val).subtract(1, 'day').format()
    }
  }

  statusTodos = {$id: -1, $tag: $.t('app.all')}
  auxStatusList: IResource[] = [this.statusTodos]
  selectedStatusList: IResource[] = [this.statusTodos]
  allAvailableStatus = [DeliveryStatus.PENDING, DeliveryStatus.FINISHED, DeliveryStatus.STARTED]

  allRegion = ItrackCollection.allRegion.prependNull('app.all')
  allStatus = ItrackCollection.allStatus.prepend(-1, $.t('app.all'))
  allEvent = ItrackCollection.allEvent.prependNull('app.all')
  allAudit = ItrackCollection.allAudit.prependNull('app.all')
  allSlaStatus = ItrackCollection.allSlaStatus.prependNull('app.all')
  allDriverStatus = ItrackCollection.allDriverStatus.prependNull('app.all')
  allDocs = new ObjectCollection()
  allTipoInsumo = ItrackCollection.allTipoInsumo.prependNull('app.all')
  allSistema = ItrackCollection.allSistema
  allTipoOcorrencia = ItrackCollection.allTipoOcorrencia
  allTipoOcorrenciaFiltro = ItrackCollection.allTipoOcorrenciaFiltro
  tipoOcorrenciaNaoDevolvidaCollection = new Collection(TipoOcorrencia)
  tipoOcorrenciaDevolvidaCollection = new Collection(TipoOcorrencia)
  allCentroDeCusto = ItrackCollection.allCentroDeCusto.prependNull('app.all')
  allAuditoriaMotivo = ItrackCollection.allAuditoriaMotivo.prependNull('app.all')
  allAuditoriaMotivoFiltro = ItrackCollection.allAuditoriaMotivoFiltro.prependNull('app.all')
  allCte = new ObjectCollection()
  allAuditGeo = ItrackCollection.allAuditGeo.prependNull('app.all')

  handleStatusInput(selectedItems: IResource[]) {
    const hasSelectedOtherThanTodos = selectedItems.length > 1 && selectedItems.find((item) => item.$id === -1)
        && this.auxStatusList.find((item) => item.$id === -1)

    const hasSelectedTodos = selectedItems.find((item) => item.$id === -1)
        && !this.auxStatusList.find((item) => item.$id === -1)

    const selectedAllStatus = this.allAvailableStatus.every((id) =>
        selectedItems.some((selectedItem) => id === selectedItem.$id),
    )

    if (hasSelectedOtherThanTodos) {
      this.selectedStatusList = this.selectedStatusList.filter((item) => item.$id !== -1)
    }

    if (selectedAllStatus || hasSelectedTodos) {
      this.selectedStatusList = [this.statusTodos]
      this.status = null
      this.auxStatusList = this.selectedStatusList
      return
    }

    this.status = this.selectedStatusList.map((item) => item.$id).join(',')
    this.auxStatusList = cloneDeep(this.selectedStatusList)
  }

  get eventResource() {
    return this.allEvent.get(this.event)
  }
  set eventResource(val: IResource | null) {
    this.event = val && val.$id as DeliveryEvent || null
  }

  get auditResource() {
    return this.allAudit.get(this.audit)
  }
  set auditResource(val: IResource | null) {
    this.audit = val && val.$id as DeliveryAudit || null
  }

  get auditGeoResource() {
      return this.allAuditGeo.get(this.auditGeo)
  }
  set auditGeoResource(val: IResource | null) {
      this.auditGeo = val && val.$id as DeliveryAuditGeo || null
  }

  get slaStatusResource() {
    return this.allSlaStatus.get(this.slaStatus)
  }
  set slaStatusResource(val: IResource | null) {
    this.slaStatus = val && val.$id as DeliverySlaStatus || null
  }

  get driverStatusResource() {
    return this.allDriverStatus.get(this.driverStatus)
  }
  set driverStatusResource(val: IResource | null) {
    this.driverStatus = val && val.$id as DeliveryDriverStatus || null
  }

  get docsResource() {
    const str = String(this.docs || '')
    const docs = str.split(',')
    return this.allDocs.getMany(docs)
  }
  set docsResource(val: IResource[]) {
    this.docs = val.map((item: IResource) => item.$id).join(',')
  }

  get regionResource() {
    return this.allRegion.get(this.region)
  }
  set regionResource(val: IResource | null) {
    this.region = val && val.$id as Region || null
  }

  get ufResource() {
    return this.ufCollection.get(this.uf)
  }
  set ufResource(val: IResource | null) {
    this.uf = val && val.$id as UF || null
  }

  get tipoOcorrenciaResource() {
    return this.tipoOcorrenciaNaoDevolvidaCollection.get(this.idTipoOcorrencia)
  }
  set tipoOcorrenciaResource(val: IResource | null) {
    this.idTipoOcorrencia = val && val.$id as number || null
  }

  get centroDeCustoResource() {
    return this.allCentroDeCusto.get(this.idCentroDeCusto)
  }
  set centroDeCustoResource(val: IResource | null) {
    this.idCentroDeCusto = val && val.$id as number || null
  }

  get auditoriaMotivoResource() {
    return this.allAuditoriaMotivo.get(this.idMotivoAuditoria)
  }
  set auditoriaMotivoResource(val: IResource | null) {
    this.idMotivoAuditoria = val && val.$id as number || null
  }

  get ufCollection() {
    return ItrackCollection.getUfCollection(this.region).prependNull('app.all')
  }

  get cteResource() {
    const str = String(this.searchByCTE || '')
    const cte = str.split(',')
    return this.allCte.getMany(cte)
  }
  set cteResource(val: IResource[]) {
    this.searchByCTE = val.map((item: IResource) => item.$id).join(',')
  }

  cleanDocs() {
    this.docs = ''
  }

  cleanCtes() {
    this.cte = ''
  }

  cleanSearchByCTE() {
    this.searchByCTE = ''
  }

  populateAllDocs(val: IResource[]) {
    this.allDocs = new ObjectCollection(val)
  }

  populateSearchByCTE(val: IResource[]) {
    this.allCte = new ObjectCollection(val)
  }

  get insumoResource() {
    return this.allTipoInsumo.get(this.idInsumo)
  }
  set insumoCustoResource(val: IResource | null) {
    this.idInsumo = val && val.$id as number || null
  }

  get tipoFinalizacaoResource() {
    return this.allSistema.get(this.idTipoFinalizacao)
  }
  set tipoFinalizacaoResource(val: IResource | null) {
    this.idTipoFinalizacao = val && val.$id as TipoSistema || null
  }

  get tipoCriacaoResource() {
    return this.allSistema.get(this.idTipoCriacao)
  }
  set tipoCriacaoResource(val: IResource | null) {
    this.idTipoCriacao = val && val.$id as TipoSistema || null
  }

  cleanDateFilters() {
    this.dataPrevisaoInicio = null
    this.dataPrevisaoFim = null
    this.dataEntregaInicio = null
    this.dataEntregaFim = null
    this.startDate = null
    this.endDate = null
  }

  get isDataFilterCleared() {
    return !this.dataPrevisaoInicio &&
        !this.dataPrevisaoFim &&
        !this.dataEntregaInicio &&
        !this.dataEntregaFim &&
        !this.startDate &&
        !this.endDate
  }
}
